import type { AbstractEngine } from '@babylonjs/core/Engines/abstractEngine';
import { Scene } from '@babylonjs/core/scene';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import type { RenderingConfig } from '../config/rendering.config';

export interface SceneBundle {
  readonly scene: Scene;
  readonly ambient: HemisphericLight;
  readonly sun: DirectionalLight;
}

/** Creates the scene with sun, fill light and fog. Sky, IBL, shadows and tone mapping: RenderingEnvironment. */
export function createScene(engine: AbstractEngine, config: RenderingConfig): SceneBundle {
  const scene = new Scene(engine);
  const clear = Color3.FromHexString(config.clearColor);
  scene.clearColor = new Color4(clear.r, clear.g, clear.b, 1);
  scene.ambientColor = new Color3(0.25, 0.25, 0.25);
  scene.skipPointerMovePicking = true;
  scene.autoClearDepthAndStencil = true;

  if (config.fog.enabled) {
    scene.fogMode = Scene.FOGMODE_LINEAR;
    scene.fogColor = Color3.FromHexString(config.fog.color);
    scene.fogStart = config.fog.start;
    scene.fogEnd = config.fog.end;
  }

  const ambient = new HemisphericLight('LIGHT_AMBIENT', new Vector3(0, 1, 0), scene);
  ambient.intensity = config.ambientLightIntensity;
  ambient.groundColor = new Color3(0.45, 0.42, 0.38);

  const [dx, dy, dz] = config.sunDirection;
  const sun = new DirectionalLight('LIGHT_SUN', new Vector3(dx, dy, dz).normalize(), scene);
  sun.intensity = config.sunIntensity;

  return { scene, ambient, sun };
}
